/**
 * Palette checks for the theme builder and the Colors tab (screenshots/80, 79).
 * A TypeScript port of the dataviz skill's validate_palette.js: same color math (OKLab, Machado 2009 CVD
 * simulation at severity 1.0, WCAG contrast) and the same thresholds, so the numbers in the UI match the
 * validator exactly (tests/chartSettings.test.ts pins a few of them).
 */

export type CheckState = "pass" | "warn" | "fail";
export interface Check { id: string; label: string; state: CheckState; detail: string }

const BAND: [number, number] = [0.43, 0.77];          // OKLCH L, light mode
const CHROMA_FLOOR = 0.10;
const CVD_TARGET = 8.0, CVD_FLOOR = 6.0;              // OKLab ΔE×100, min(protan, deutan), adjacent pairs
const NORMAL_FLOOR = 15.0;                            // OKLab ΔE×100, unsimulated
const CONTRAST_MIN = 3.0;
export const DEFAULT_SURFACE = "#fcfcfb";
const ORDINAL_MIN_DL = 0.06, ORDINAL_LIGHT_FLOOR = 2.0;

const MACHADO = {
  protan: [[0.152286, 1.052583, -0.204868], [0.114503, 0.786281, 0.099216], [-0.003882, -0.048116, 1.051998]],
  deutan: [[0.367322, 0.860646, -0.227968], [0.280085, 0.672501, 0.047413], [-0.01182, 0.04294, 0.968881]],
  tritan: [[1.255528, -0.076749, -0.178779], [-0.078411, 0.930809, 0.147602], [0.004733, 0.691367, 0.3039]],
} as const;
export type Vision = keyof typeof MACHADO;

export const isHex = (v: string) => /^#?[0-9a-fA-F]{6}$/.test(v.trim());
const srgb = (h: string) => { const x = h.trim().replace(/^#/, ""); return [0, 2, 4].map((i) => parseInt(x.slice(i, i + 2), 16) / 255); };
const s2lin = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
const lin2s = (c: number) => { c = Math.max(0, Math.min(1, c)); return c <= 0.0031308 ? 12.92 * c : 1.055 * c ** (1 / 2.4) - 0.055; };
const lin = (h: string) => srgb(h).map(s2lin) as [number, number, number];
const relLum = (h: string) => { const [r, g, b] = lin(h); return 0.2126 * r + 0.7152 * g + 0.0722 * b; };

/** WCAG contrast ratio. */
export const contrast = (a: string, b: string) => { const [hi, lo] = [relLum(a), relLum(b)].sort((x, y) => y - x); return (hi + 0.05) / (lo + 0.05); };

function oklabFromLin([r, g, b]: number[]) {
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b);
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b);
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b);
  return [0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s, 1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s, 0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s];
}
const oklab = (h: string) => oklabFromLin(lin(h));
export const oklch = (h: string) => { const [L, a, b] = oklab(h); return { L, C: Math.hypot(a, b), H: ((Math.atan2(b, a) * 180) / Math.PI % 360 + 360) % 360 }; };

const simLin = (h: string, kind: Vision) => { const [r, g, b] = lin(h), M = MACHADO[kind]; return M.map((row) => Math.max(0, Math.min(1, row[0] * r + row[1] * g + row[2] * b))); };

/** A color as someone with this vision type sees it (hex), or grayscale. Used by the "See it as" previews. */
export function simulate(h: string, kind: Vision | "gray"): string {
  const out = kind === "gray" ? Array(3).fill(relLum(h)) : simLin(h, kind);
  return "#" + out.map((c) => Math.round(lin2s(c) * 255).toString(16).padStart(2, "0")).join("").toUpperCase();
}

/** Euclidean distance in OKLab ×100; no kind = normal vision. */
export function deltaE(a: string, b: string, kind?: Vision) {
  const x = oklabFromLin(kind ? simLin(a, kind) : lin(a)), y = oklabFromLin(kind ? simLin(b, kind) : lin(b));
  return 100 * Math.hypot(x[0] - y[0], x[1] - y[1], x[2] - y[2]);
}

const pairsOf = (n: number, all: boolean) => all
  ? Array.from({ length: n }, (_, i) => Array.from({ length: n - i - 1 }, (_, k) => [i, i + 1 + k] as [number, number])).flat()
  : Array.from({ length: Math.max(0, n - 1) }, (_, i) => [i, i + 1] as [number, number]);

/** Category colors. `pairs: "all"` for scatter, maps and small multiples. */
export function checkCategorical(palette: string[], { surface = DEFAULT_SURFACE, pairs = "adjacent" as "adjacent" | "all" } = {}): Check[] {
  const out: Check[] = [];
  const off = palette.filter((c) => { const L = oklch(c).L; return L < BAND[0] || L > BAND[1]; });
  out.push({ id: "band", label: "Lightness band", state: off.length ? "fail" : "pass",
    detail: off.length ? `Too light or dark: ${off.join(", ")}` : `All ${palette.length} inside L ${BAND[0]}–${BAND[1]}` });
  const gray = palette.filter((c) => oklch(c).C < CHROMA_FLOOR);
  out.push({ id: "chroma", label: "Chroma floor", state: gray.length ? "fail" : "pass",
    detail: gray.length ? `Reads as gray: ${gray.map((c) => `${c} (${oklch(c).C.toFixed(3)})`).join(", ")}` : `All ${palette.length} ≥ ${CHROMA_FLOOR}` });

  const pl = pairsOf(palette.length, pairs === "all");
  let worst: { d: number; kind: Vision; i: number; j: number } | null = null;
  for (const kind of ["protan", "deutan"] as const) for (const [i, j] of pl) {
    const d = deltaE(palette[i], palette[j], kind);
    if (!worst || d < worst.d) worst = { d, kind, i, j };
  }
  const cvd = worst ? (worst.d >= CVD_TARGET ? "pass" : worst.d >= CVD_FLOOR ? "warn" : "fail") : "pass";
  out.push({ id: "cvd", label: "Color-blind separation", state: cvd,
    detail: worst ? `Colors ${worst.i + 1} and ${worst.j + 1}: ΔE ${worst.d.toFixed(1)} (${worst.kind})${cvd === "warn" ? " · needs direct labels" : ""}` : "n/a" });

  let nw: { d: number; i: number; j: number } | null = null;
  for (const [i, j] of pl) { const d = deltaE(palette[i], palette[j]); if (!nw || d < nw.d) nw = { d, i, j }; }
  out.push({ id: "normal", label: "Normal-vision floor", state: !nw || nw.d >= NORMAL_FLOOR ? "pass" : "fail",
    detail: nw ? `Colors ${nw.i + 1} and ${nw.j + 1}: ΔE ${nw.d.toFixed(1)}${nw.d >= NORMAL_FLOOR ? "" : ` · needs ${NORMAL_FLOOR}+`}` : "n/a" });

  const low = palette.filter((c) => contrast(c, surface) < CONTRAST_MIN);
  out.push({ id: "contrast", label: "Contrast vs surface", state: low.length ? "warn" : "pass",
    detail: low.length ? `Below 3:1, needs labels or a table view: ${low.join(", ")}` : `All ${palette.length} ≥ 3:1` });
  return out;
}

/** Sequential ramps (maps, heatmaps, year calendar). */
export function checkSequential(ramp: string[], { surface = DEFAULT_SURFACE } = {}): Check[] {
  const Ls = ramp.map((c) => oklch(c).L);
  const order = [...Ls.keys()].sort((a, b) => Ls[a] - Ls[b]);
  const mono = order.every((v, i) => v === i) || order.every((v, i) => v === Ls.length - 1 - i);
  const thin = Ls.slice(1).map((l, i) => Math.abs(l - Ls[i])).filter((g) => g < ORDINAL_MIN_DL).length;
  const lightest = [...ramp].sort((a, b) => oklch(b).L - oklch(a).L)[0];
  const cr = lightest ? contrast(lightest, surface) : 99;
  const hues = ramp.map((c) => oklch(c).H);
  let spread = hues.length ? Math.max(...hues) - Math.min(...hues) : 0;
  if (spread > 180) spread = 360 - spread;
  return [
    { id: "monotone", label: "Light → dark", state: mono ? "pass" : "fail", detail: mono ? "Steps read light to dark" : "Steps are out of order" },
    { id: "gaps", label: "Even steps", state: thin ? "fail" : "pass", detail: thin ? `${thin} step(s) too close` : `All gaps ≥ ${ORDINAL_MIN_DL}` },
    { id: "lightEnd", label: "Light end visible", state: cr >= ORDINAL_LIGHT_FLOOR ? "pass" : "fail", detail: `${lightest} at ${cr.toFixed(2)}:1` },
    { id: "hue", label: "One hue", state: spread <= 40 ? "pass" : "fail", detail: `Hue spread ${spread.toFixed(0)}°` },
  ];
}

export const worstState = (checks: Check[]): CheckState =>
  checks.some((c) => c.state === "fail") ? "fail" : checks.some((c) => c.state === "warn") ? "warn" : "pass";
